import { axiosWithoutAuth } from "../axios/axiosInstance";

// Sends a crash/error alert to the team via the dedicated backend endpoint
// MailSend/SendCrashMail (public — no auth). Modeled on
// src/View/CustomerFeedback/feedbackApi.js. The backend reads userId,
// currenturl and downloadrequire; the error message + stack + browser +
// timestamp ride in `downloadrequire`.

const SENT_KEY = "__errAlertsSent";

// A short, stable fingerprint so the same error doesn't email twice per session.
const signatureOf = (message) => {
  const path = (() => {
    try {
      return window.location.pathname;
    } catch (e) {
      return "";
    }
  })();
  return `${path}::${String(message || "unknown").slice(0, 140)}`;
};

// Returns the set of signatures already alerted this session.
const readSent = () => {
  try {
    return JSON.parse(sessionStorage.getItem(SENT_KEY)) || [];
  } catch (e) {
    return [];
  }
};

const markSent = (signature) => {
  try {
    const list = readSent();
    if (!list.includes(signature)) {
      list.push(signature);
      sessionStorage.setItem(SENT_KEY, JSON.stringify(list));
    }
  } catch (e) {
    /* sessionStorage unavailable — best-effort throttle only */
  }
};

// Pull whatever we can out of the various shapes an error can arrive in
// (Error object, string, event reason, etc.).
const describe = (errorInfo) => {
  const err =
    errorInfo && errorInfo.error !== undefined ? errorInfo.error : errorInfo;
  let message = "";
  let stack = "";
  if (err instanceof Error) {
    message = err.message;
    stack = err.stack || "";
  } else if (typeof err === "string") {
    message = err;
  } else if (err && typeof err === "object") {
    message = err.message || JSON.stringify(err);
    stack = err.stack || "";
  } else {
    message = String(err);
  }
  return { message, stack };
};

/**
 * Fire-and-forget alert. Never throws — it runs from inside the error boundary
 * and global handlers, where a throw would compound the failure.
 * @param {{error:any, componentStack?:string, source?:string}} errorInfo
 * @returns {Promise<boolean>} true when the backend confirms the mail ("1")
 */
export const sendErrorAlert = async (errorInfo = {}) => {
  try {
    const { message, stack } = describe(errorInfo);
    const signature = signatureOf(message);

    // Throttle: one alert per unique error per session.
    if (readSent().includes(signature)) return false;
    markSent(signature);

    const userId = (() => {
      try {
        const id = String(sessionStorage.getItem("UserID"));
        return id === "null" || id === "undefined" ? "999" : id;
      } catch (e) {
        return "999";
      }
    })();

    const details = [
      `Source: ${errorInfo.source || "react-render"}`,
      `Message: ${message}`,
      `URL: ${safe(() => window.location.href)}`,
      `Time: ${new Date().toISOString()}`,
      `Browser: ${safe(() => navigator.userAgent)}`,
      errorInfo.componentStack
        ? `Component stack:${errorInfo.componentStack}`
        : "",
      stack ? `Stack:\n${stack}` : "",
    ]
      .filter(Boolean)
      .join("\n");

    const payload = {
      userId,
      fromid: "noreply@wes.com.au",
      familyName: "",
      currenturl: safe(() => window.location.href),
      fullname: "⚠ SITE CRASH ALERT",
      phone: "",
      downloadrequire: details,
    };

    const res = await axiosWithoutAuth.post("MailSend/SendCrashMail", payload);
    return res && String(res.data).trim() === "1";
  } catch (e) {
    // Swallow — alerting must never surface a new error.
    return false;
  }
};

// Small helper so a broken global (e.g. no navigator) can't throw here.
function safe(fn) {
  try {
    return fn();
  } catch (e) {
    return "";
  }
}
